import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatBytes } from "@/lib/format";
import { Download, ExternalLink, ZoomIn, ZoomOut, FileWarning, FileText } from "lucide-react";

/** Viser bilagsfilen (PDF i iframe, bilde med zoom, EHF/XML som tekst). Henter via /attachments/{id}/file. */
export const AttachmentViewer = ({ attachment }) => {
  const [url, setUrl] = useState(null);
  const [text, setText] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(1);
  const id = attachment?.id;
  const contentType = attachment?.content_type || "";

  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;
    async function load() {
      if (!id) { setLoading(false); return; }
      setLoading(true); setError(null); setText(null); setUrl(null);
      try {
        const res = await api.get(`/attachments/${id}/file`, { responseType: "blob" });
        if (cancelled) return;
        if (contentType.includes("xml")) setText(await res.data.text());
        else { objectUrl = URL.createObjectURL(res.data); setUrl(objectUrl); }
      } catch {
        if (!cancelled) setError("Kunne ikke laste filen.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [id, contentType]);

  const download = () => {
    const a = window.document.createElement("a");
    a.href = url || URL.createObjectURL(new Blob([text || ""], { type: "application/xml" }));
    a.download = attachment?.filename || "bilag";
    a.click();
  };

  const isPdf = contentType.includes("pdf");
  const isImage = contentType.startsWith("image/");

  return (
    <div className="flex flex-col h-full rounded-xl border bg-secondary overflow-hidden" data-testid="attachment-viewer">
      <div className="flex items-center justify-between gap-2 border-b bg-card px-3 py-2">
        <div className="min-w-0">
          <p className="text-sm font-medium truncate" title={attachment?.filename}>{attachment?.filename}</p>
          <p className="text-xs text-muted-foreground">{isPdf ? "PDF" : isImage ? "Bilde" : "EHF / XML"}{attachment?.size ? ` · ${formatBytes(attachment.size)}` : ""}{attachment?.source_path ? ` · ${attachment.source_path}` : ""}</p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {isImage ? (
            <>
              <Button variant="ghost" size="icon" onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))} aria-label="Zoom ut" data-testid="attachment-zoom-out"><ZoomOut className="h-4 w-4" /></Button>
              <Button variant="ghost" size="icon" onClick={() => setZoom((z) => Math.min(3, z + 0.25))} aria-label="Zoom inn" data-testid="attachment-zoom-in"><ZoomIn className="h-4 w-4" /></Button>
            </>
          ) : null}
          {url ? <Button variant="ghost" size="icon" asChild aria-label="Åpne i ny fane"><a href={url} target="_blank" rel="noreferrer" data-testid="attachment-open-new-tab"><ExternalLink className="h-4 w-4" /></a></Button> : null}
          <Button variant="ghost" size="icon" onClick={download} disabled={!url && text === null} aria-label="Last ned" data-testid="attachment-download"><Download className="h-4 w-4" /></Button>
        </div>
      </div>
      <div className="flex-1 min-h-[320px] overflow-auto">
        {loading ? (
          <div className="p-4 space-y-3"><Skeleton className="h-6 w-1/2" /><Skeleton className="h-4 w-3/4" /><Skeleton className="h-64 w-full" /></div>
        ) : error ? (
          <div className="h-full flex flex-col items-center justify-center p-6 text-center"><FileWarning className="h-8 w-8 text-destructive mb-2" /><p className="font-medium">{error}</p></div>
        ) : text !== null ? (
          <div className="min-h-full bg-card">
            <div className="px-4 py-3 border-b bg-accent text-accent-foreground text-sm flex items-start gap-2"><FileText className="h-4 w-4 mt-0.5 shrink-0" /><p>Elektronisk faktura (EHF / UBL 2.1). Feltene er lest direkte fra filen uten AI.</p></div>
            <pre className="p-4 text-xs leading-5 whitespace-pre-wrap break-all" data-testid="attachment-xml">{text}</pre>
          </div>
        ) : isPdf ? (
          <iframe title="Bilag" src={`${url}#toolbar=0&navpanes=0`} className="w-full h-full min-h-[520px] bg-white" data-testid="attachment-pdf-frame" />
        ) : (
          <div className="p-4 flex justify-center"><img src={url} alt={attachment?.filename || "Bilag"} style={{ width: `${zoom * 100}%`, maxWidth: "none" }} className="rounded-md shadow-sm bg-white" data-testid="attachment-image" /></div>
        )}
      </div>
    </div>
  );
};
